import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import styled from 'styled-components';
import isNil from 'lodash/isNil';

import {
  Typography as MuiTypography,
  Theme,
  Tooltip,
  TableCell,
  TableRow,
  Box,
  Collapse,
  IconButton,
  Chip,
} from '@mui/material';
import { Alert, AlertProps } from '@mui/material';
import { spacing } from '@mui/system';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';

import { ClickableRow } from 'styles';

import { DATA_TYPES, integrationHealthIsoStateIds, dataTypes } from '@constants';

import { selectTeamHash, selectProcessedServiceMetricsData } from 'selectors';

import {
  isDataTypePending,
  convertISODateToRegularDateString,
  calculateLatestDate,
  isIntegrationHealthy,
  renderIntegrationHealthStatus,
  getIntegrationHealthAlertDisplay,
  renderIntegrationHealthByRequestAndSuccessCountPercent,
} from 'utils';
import { getSyncRatioAlertDisplay } from 'utils/display';
import useAppNavgation from 'hooks/useAppNavigation';
import useIntegrationHealthData from 'hooks/useIntegrationHealthData';

import { TelemetryLastUpdatesType, TargetService } from 'types';
import * as IntegrationTypes from 'IntegrationModule/types';

import { StyledAlert } from 'shared/styles';

const Typography = styled(MuiTypography)(spacing);

const DataTipContainer = styled.div``;

type useServiceMetricsTableProps = {
  currentService: TargetService;
  selectedInterval: string; // "day", "week", "month"
};

const calculateLatestLastUpdateDate = (data: Record<string, TelemetryLastUpdatesType>): string => {
  if (!data) return '';
  const datesArray = Object.values(data).map((d) => d.updated_at);
  return convertISODateToRegularDateString(calculateLatestDate(datesArray));
};

const useServiceMetricsTable = ({
  currentService,
  selectedInterval,
}: useServiceMetricsTableProps) => {
  const dispatch = useDispatch();
  const { navigateToIntegrationDetailsPage, navigateToIntegrationHealth } = useAppNavgation();
  const {
    data: processedServiceMetricsData,
    error,
    isRequesting: isFetchingServiceMetricsData,
  } = useSelector(selectProcessedServiceMetricsData);
  const teamHash = useSelector(selectTeamHash);
  const {
    data: syncRatioTableIntegrationsHealthData,
    isRequesting: isRequestingSyncRatioTableIntegrationsHealthData,
  } = useIntegrationHealthData({
    isoStateId: integrationHealthIsoStateIds.SyncRatioTable,
  });

  const isRequesting =
    isRequestingSyncRatioTableIntegrationsHealthData || isFetchingServiceMetricsData;

  // Track expanded rows
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());

  const onToggleExpand = useCallback((id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  }, []);

  /** Pagination (unused) */
  const [page, setPage] = useState(0);
  const [count, setCount] = useState(-1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  // const hasReachTheEnd =
  //   overallIntegrationServiceMetricsData &&
  //   overallIntegrationServiceMetricsData[currentService].hasReachTheEnd;

  // const isAtBeginning =
  //   overallIntegrationServiceMetricsData &&
  //   overallIntegrationServiceMetricsData[currentService].offset === 0;

  const onRowChangePerPage = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // const onPageChange = (
  //   event: React.MouseEvent<HTMLButtonElement, MouseEvent> | null,
  //   newPage: number
  // ) => {
  //   if (!overallIntegrationServiceMetricsData) return;
  //   const {
  //     data,
  //   }: IntegrationServiceMetricsDataInterface = overallIntegrationServiceMetricsData;
  //   if (
  //     data
  //     // integrationData
  //     // integrationData.total_rows > integrationData.limit &&
  //     // newPage >= integrationData.offsetSofar
  //     // (rowsPerPage * newPage) + 1 >= integrationData.offset
  //   ) {
  //     /** Currently fetching all targetService ids */
  //     dispatch(
  //       fetchIntegrationServiceMetrics({
  //         isInitialFetch: false,
  //         offset: INTEGRATION_SERVICE_METRICS_API_DEFAULT_VALUES.OFFSET,
  //         limit: INTEGRATION_SERVICE_METRICS_API_DEFAULT_VALUES.LIMIT,
  //         interval: selectedInterval,
  //         targetService: currentService as string,
  //       })
  //     );
  //   }
  //   setPage(newPage);
  // };

  /****** Logic ******/

  const tableData = useMemo(() => {
    return Object.values(processedServiceMetricsData).map((processedData: Record<string, any>) => {
      const { targetServiceId, mosaicTeamId, ...dataTypeMetrics } = processedData;
      const integrationHealth =
        syncRatioTableIntegrationsHealthData[currentService]?.[targetServiceId] || {};

      return {
        targetServiceId,
        mosaicTeamId,
        integrationHealth,
        dataTypeMetrics,
      };
    });
  }, [processedServiceMetricsData, syncRatioTableIntegrationsHealthData, currentService]);

  const _customRowProps = useMemo(
    () => ({
      expandedIds,
      onToggleExpand,
      teamHash,
      currentService,
      navigateToIntegrationDetailsPage,
      navigateToIntegrationHealth,
    }),
    [
      expandedIds,
      onToggleExpand,
      teamHash,
      currentService,
      navigateToIntegrationDetailsPage,
      navigateToIntegrationHealth,
    ],
  );

  return {
    page,
    count,
    rowsPerPage,
    // onPageChange,
    onRowChangePerPage,
    tableData,
    _customRowProps,
    isIntegrationServiceMetricsTableLoading: isRequesting,
    integrationServiceMetricsTableError: error?.message,
  };
};

export default useServiceMetricsTable;
